interface Props {
  lang: "en" | "ru";
}

const pillarsEn = [
  {
    icon: "ri-scan-2-line",
    tag: "1:1 Cloning · 3D Scanning",
    title: "High-Precision Reverse Engineering",
    body: "No blueprints needed. Send us your original sample, and our engineering team will execute 100% precise 1:1 reverse engineering for mass production.",
    highlight: "3D Scan → 1:1 OEM Clone",
    num: "01",
  },
  {
    icon: "ri-links-line",
    tag: "In-house Foundry · CNC Machining",
    title: "Integrated 1+1 Production Synergy",
    body: "We bridge the gap between casting and finishing. Our integrated model ensures zero structural defects and micron-level precision for every engine block.",
    highlight: "Full-Cycle In-House",
    num: "02",
  },
  {
    icon: "ri-award-line",
    tag: "Automotive Standards · Thermal Stability",
    title: "EAC & ISO Certified Durability",
    body: "Built for extreme conditions. Every block undergoes rigorous thermal and stress testing, meeting EAC and ISO 9001:2015 standards for international markets.",
    highlight: "EAC + ISO 9001:2015",
    num: "03",
  },
  {
    icon: "ri-ship-2-line",
    tag: "Wholesale Engine Parts · Logistics",
    title: "Scalable Global Supply Chain",
    body: "High-volume manufacturing capacity with fast fulfillment from China's major ports. We handle the complexity of B2B international logistics for you.",
    highlight: "FCL/LCL · Guangzhou Hub",
    num: "04",
  },
];

const pillarsRu = [
  {
    icon: "ri-scan-2-line",
    tag: "Клонирование 1:1 · 3D-сканирование",
    title: "Высокоточный реверс-инжиниринг",
    body: "Чертежи не нужны. Пришлите нам оригинальный образец, и наша инженерная команда выполнит 100% точный реверс-инжиниринг 1:1 для серийного производства.",
    highlight: "3D-скан → клон OEM 1:1",
    num: "01",
  },
  {
    icon: "ri-links-line",
    tag: "Собственный литейный завод · ЧПУ-обработка",
    title: "Интегрированная синергия производства 1+1",
    body: "Мы устраняем разрыв между литьём и финишной обработкой. Наша интегрированная модель обеспечивает нулевые структурные дефекты и микронную точность каждого блока.",
    highlight: "Полный цикл внутри компании",
    num: "02",
  },
  {
    icon: "ri-award-line",
    tag: "Автомобильные стандарты · Термостабильность",
    title: "Долговечность с сертификатами EAC и ISO",
    body: "Создано для экстремальных условий. Каждый блок проходит строгие тепловые и нагрузочные испытания, соответствуя стандартам EAC и ISO 9001:2015 для международных рынков.",
    highlight: "EAC + ISO 9001:2015",
    num: "03",
  },
  {
    icon: "ri-ship-2-line",
    tag: "Оптовые запчасти · Логистика",
    title: "Масштабируемая глобальная цепочка поставок",
    body: "Высокообъёмные производственные мощности с быстрой отгрузкой из крупных портов Китая. Мы берём на себя сложность международной B2B-логистики.",
    highlight: "FCL/LCL · Гуанчжоу",
    num: "04",
  },
];

export default function K4MAdvantages({ lang }: Props) {
  const isRu = lang === "ru";
  const pillars = isRu ? pillarsRu : pillarsEn;

  return (
    <section className="py-24 px-6" style={{ background: "#0d1525" }}>
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}
          >
            <i className="ri-award-line text-xs" style={{ color: "#F59E0B" }} />
            <span className="text-xs tracking-widest uppercase font-medium" style={{ color: "#F59E0B", letterSpacing: "0.2em" }}>
              {isRu ? "Стратегические преимущества" : "Strategic Advantages"}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-thin text-white mb-4" style={{ letterSpacing: "0.05em" }}>
            {isRu ? "ПОЧЕМУ CHIRUNNEXA" : "WHY CHIRUNNEXA"}
            <br />
            <span style={{ color: "#F59E0B" }}>
              {isRu ? "ДЛЯ ПРОИЗВОДСТВА БЛОКОВ" : "FOR ENGINE BLOCK MANUFACTURING"}
            </span>
          </h2>
          <div className="mx-auto mt-4" style={{ width: "60px", height: "2px", background: "#F59E0B" }} />
        </div>

        {/* 4-Grid Strategy Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {pillars.map((pillar) => (
            <div
              key={pillar.num}
              className="rounded-2xl p-8 flex flex-col relative overflow-hidden transition-all duration-300"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(245,158,11,0.3)";
                (e.currentTarget as HTMLDivElement).style.background = "rgba(245,158,11,0.04)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,0.04)";
              }}
            >
              {/* Number watermark */}
              <div
                className="absolute top-4 right-5 font-black select-none"
                style={{ fontSize: "72px", color: "rgba(245,158,11,0.05)", lineHeight: 1 }}
              >
                {pillar.num}
              </div>

              {/* Icon */}
              <div
                className="w-14 h-14 flex items-center justify-center rounded-xl mb-6 shrink-0"
                style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)" }}
              >
                <i className={`${pillar.icon} text-2xl`} style={{ color: "#F59E0B" }} />
              </div>

              {/* Keyword tag */}
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4 self-start"
                style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", color: "#F59E0B" }}
              >
                <i className="ri-price-tag-3-line text-xs" />
                {pillar.tag}
              </span>

              {/* Title */}
              <h3 className="text-lg font-semibold text-white mb-3" style={{ letterSpacing: "0.02em" }}>
                {pillar.title}
              </h3>
              <div className="mb-4" style={{ width: "36px", height: "1.5px", background: "#F59E0B" }} />

              {/* Body */}
              <p className="text-sm font-light flex-1" style={{ color: "rgba(255,255,255,0.6)", lineHeight: "1.85" }}>
                {pillar.body}
              </p>

              {/* Highlight badge */}
              <div className="mt-6 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#F59E0B" }} />
                  <span className="text-xs font-bold tracking-wide" style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em" }}>
                    {pillar.highlight}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {(isRu
            ? [
                { value: "1:1", label: "Точность клонирования" },
                { value: "3D", label: "Реверс-инжиниринг" },
                { value: "EAC", label: "ISO 9001:2015" },
                { value: "FCL", label: "Глобальные поставки" },
              ]
            : [
                { value: "1:1", label: "Clone Precision" },
                { value: "3D", label: "Reverse Engineering" },
                { value: "EAC", label: "ISO 9001:2015" },
                { value: "FCL", label: "Global Supply" },
              ]
          ).map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center py-8 px-4 text-center"
              style={{ borderRight: "1px solid rgba(255,255,255,0.06)" }}
            >
              <p className="text-2xl font-thin mb-1" style={{ letterSpacing: "0.05em", color: "#F59E0B" }}>
                {stat.value}
              </p>
              <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.12em", fontSize: "9px" }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
